/**
 * Lykke for clients: the wire to cowork `/v1/lykke/*` (backend routes/lykke.ts). Same base URL and auth as every
 * cowork call (the person's own Beatly JWT), so everything Lykke reads is what this person may see.
 *
 * Decisions are not made here: a decision card calls the existing api-v2 client endpoints (beatlyApi.ts) with the
 * person's token, so the platform records the client's own decision.
 */
import { API_BASE_URL, authHeaders } from '@/cowork/omniad/api/coworkApi';

export type NeedKind = 'brief' | 'creators' | 'drafts' | 'payments' | 'question' | 'status';
export interface NeedAction { label: string; page?: string; params?: Record<string, string>; focus?: Record<string, string>; href?: string }
export interface NeedItem {
  key: string; kind: NeedKind; campaignId?: string; campaignName?: string;
  title: string; detail?: string; count: number; due?: string; dueLabel?: string; urgent: boolean;
  action: NeedAction; decision?: 'creators' | 'drafts' | 'payments'; source: string;
}
export interface LykkeMessage { id: string; role: 'user' | 'assistant' | 'event'; text: string; meta?: Record<string, unknown> | null; createdAt: string }
export interface ActiveCampaign { id: string; name: string; brand?: string; stage: string; status: string; /** An order ("Order a campaign") Beatly is still setting up: opens the order. */ orderId?: string; next?: { date: string; label: string; what: string } }
export interface LykkeHomeData {
  thread: { id: string; campaignId: string | null };
  messages: LykkeMessage[];
  needs: NeedItem[];
  campaigns: ActiveCampaign[];
  accountManager: { name: string } | null;
}
export interface DecisionItem { id: string; title: string; sub?: string; preview?: string; productId?: string; influencerId?: string }
export interface DecisionCardData { kind: 'creators' | 'drafts' | 'payments'; campaignId: string; campaignName: string; items: DecisionItem[] }
export interface RequestEvent { id: string; topic: string; who: string; by: string; campaignName?: string }
export interface NavigateEvent { page: string; params: Record<string, string>; focus?: Record<string, string>; by: string }

export type LykkeEvent =
  | { event: 'thread'; data: { id: string; campaignId: string | null } }
  | { event: 'working'; data: { tool: string } }
  | { event: 'navigate'; data: NavigateEvent }
  | { event: 'decision'; data: DecisionCardData }
  | { event: 'request'; data: RequestEvent }
  | { event: 'say'; data: { text: string; spoken: string } }
  | { event: 'done'; data: { ms: number; steps?: number } }
  | { event: 'error'; data: { message: string } };

/** A file the person attaches to a message (base64, read in that turn only). */
export interface LykkeFileIn { name: string; mime: string; data: string }

export interface LykkeTurnBody {
  files?: LykkeFileIn[];
  text: string; campaignId?: string | null; mode: 'chat' | 'voice'; brandId?: string;
  pages: unknown[]; here?: { page: string; label?: string; path?: string } | null;
}

async function json<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE_URL}/v1/lykke${path}`, { ...init, headers: { ...authHeaders(true), ...(init?.headers || {}) } });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((body as { error?: string }).error || `Error ${res.status}`);
  return body as T;
}

export const lykkeApi = {
  home: () => json<LykkeHomeData>('/home'),
  needsYou: (campaignId?: string | null) => json<{ items: NeedItem[] }>(`/needs-you${campaignId ? `?campaignId=${encodeURIComponent(campaignId)}` : ''}`),
  thread: (campaignId?: string | null) => json<{ thread: { id: string; campaignId: string | null; title?: string }; messages: LykkeMessage[] }>(`/thread${campaignId ? `?campaignId=${encodeURIComponent(campaignId)}` : ''}`),
  threads: () => json<{ threads: Array<{ id: string; campaignId: string | null; title: string; updatedAt: string; lastText: string | null }> }>('/threads'),
  seen: (requestId: string) => json<{ ok: boolean }>(`/requests/${encodeURIComponent(requestId)}/seen`, { method: 'POST', body: '{}' }),
  settings: () => json<{ urgent: boolean; digest: boolean; email: string | null; delivers: boolean }>('/settings'),
  saveSettings: (s: { urgent?: boolean; digest?: boolean }) => json<{ urgent: boolean; digest: boolean; email: string | null; delivers: boolean }>('/settings', { method: 'PUT', body: JSON.stringify(s) }),
  /** One line in Lykke's voice (mp3), or null when there is no voice here. */
  say: async (text: string): Promise<Blob | null> => {
    const res = await fetch(`${API_BASE_URL}/v1/lykke/say`, { method: 'POST', headers: authHeaders(true), body: JSON.stringify({ text }) });
    return res.status === 200 ? res.blob() : null;
  },
};

/** Runs one turn and hands each event to `on` as it arrives. Resolves when the stream ends. */
export async function lykkeTurn(body: LykkeTurnBody, on: (e: LykkeEvent) => void, signal?: AbortSignal): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}/v1/lykke/turn`, { method: 'POST', headers: authHeaders(true), body: JSON.stringify(body), signal });
  } catch {
    on({ event: 'error', data: { message: 'Lykke is not reachable right now. Try again.' } });
    return;
  }
  if (!res.ok || !res.body) {
    on({ event: 'error', data: { message: res.status === 401 ? 'Sign in again to talk to Lykke.' : 'Lykke is not reachable right now. Try again.' } });
    return;
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let cut: number;
    while ((cut = buf.indexOf('\n\n')) >= 0) {
      const chunk = buf.slice(0, cut); buf = buf.slice(cut + 2);
      const event = /^event: (.+)$/m.exec(chunk)?.[1]?.trim();
      const data = /^data: (.*)$/m.exec(chunk)?.[1];
      if (!event || data === undefined) continue;
      try { on({ event, data: JSON.parse(data) } as LykkeEvent); } catch { /* a malformed line is skipped */ }
    }
  }
}
